// web/src/components/auth/useResendVerification.ts
//
// Shared hook for the two "resend verification email" entry points
// (SignupForm success state and ConfirmHandler error state). Wraps the
// Supabase `resend` call with a 60-second client-side cooldown that
// matches Supabase's server-side rate limit, so users don't hammer the
// button and get a 429.
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";

const COOLDOWN_SECONDS = 60;

export type ResendStatus = "idle" | "sending" | "sent" | "error";

export interface UseResendVerificationResult {
  resend: (email: string) => Promise<void>;
  status: ResendStatus;
  /** Seconds remaining until the user can resend again. 0 when not cooling down. */
  cooldownRemaining: number;
  /** Error message from the most recent failed attempt, if any. */
  error: string | null;
}

export function useResendVerification(): UseResendVerificationResult {
  const [status, setStatus] = useState<ResendStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const cooldownTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup any in-flight cooldown timer on unmount so we don't leak
  // setInterval handles across StrictMode remounts in dev.
  useEffect(() => {
    return () => {
      if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    };
  }, []);

  const startCooldown = useCallback((seconds: number) => {
    setCooldownRemaining(seconds);
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    cooldownTimer.current = setInterval(() => {
      setCooldownRemaining((remaining) => {
        if (remaining <= 1) {
          if (cooldownTimer.current) clearInterval(cooldownTimer.current);
          cooldownTimer.current = null;
          return 0;
        }
        return remaining - 1;
      });
    }, 1000);
  }, []);

  const resend = useCallback(
    async (email: string) => {
      if (cooldownRemaining > 0) return;
      setStatus("sending");
      setError(null);
      const { error: err } = await supabase.auth.resend({
        type: "signup",
        email,
      });
      if (err) {
        setStatus("error");
        setError(err.message);
        // Even on error (e.g. rate-limited), start a cooldown so the next
        // click can't happen for at least the standard window.
        startCooldown(COOLDOWN_SECONDS);
        return;
      }
      setStatus("sent");
      startCooldown(COOLDOWN_SECONDS);
    },
    [cooldownRemaining, startCooldown],
  );

  return { resend, status, cooldownRemaining, error };
}
